import React, { useState, useEffect, useRef } from 'react';
import { Eye, RotateCcw, Play, Pause, ArrowUpDown, AlertTriangle, CheckCircle2 } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · O2 · Conduite commentée avant/arrière + rétro intérieur
// Œuvre pédagogique interactive :
//  · Vue conducteur (route AVANT) + rétroviseur intérieur (route ARRIÈRE)
//  · Timer 10 s : rappel du coup d'œil rétro
//  · Bouton "Coup d'œil rétro" → SWAP visuel (bref éclairage du rétro)
//  · 3 scénarios choisis : trafic normal / voiture qui colle / moto qui remonte
//  · Diagnostic : ce qu'on vient de voir + geste adapté
// ══════════════════════════════════════════════════════════════════════════

const SCENARIOS = [
  {
    id: 'normal',
    label: 'Trafic fluide',
    color: '#10b981',
    behind: {
      title: 'Rien derrière',
      details: 'Aucun véhicule dans les 100 m qui suivent. Route dégagée.',
      cars: [], // dessin custom
    },
    diagnostic: "Situation normale : tu conserves ton allure. Coup d'œil rétro toutes les 10 s pour vérifier qu'aucun véhicule rapide n'arrive.",
    geste: "Continue à surveiller — anticipation permanente. Ne pas relâcher la vigilance parce que la route est dégagée.",
  },
  {
    id: 'colle',
    label: 'Voiture qui colle',
    color: '#f59e0b',
    behind: {
      title: 'Véhicule collé (< 1 s)',
      details: "Une berline suit à moins d'1 seconde. Distance de sécurité NON respectée par le suiveur.",
      cars: [
        { type: 'car', dist: 15, lane: 'center', color: '#dc2626' },
      ],
    },
    diagnostic: "Le suiveur ne respecte pas les 2 s de sécurité. Si TU freines fort, il te percute par l'arrière = coup du lapin garanti.",
    geste: "Augmente TA distance devant (passe à 3 s au lieu de 2). Si possible, change de file pour le laisser passer. NE FREINE PAS pour lui « faire la leçon ».",
  },
  {
    id: 'moto',
    label: 'Moto qui remonte',
    color: '#dc2626',
    behind: {
      title: 'Moto en interfile',
      details: "Un 2-roues remonte entre 2 files, il approche vite. Souvent invisible dans le rétro extérieur.",
      cars: [
        { type: 'moto', dist: 25, lane: 'left', color: '#8b5cf6' },
      ],
    },
    diagnostic: "En cas de changement de file GAUCHE sans regarder l'intérieur PUIS l'extérieur PUIS l'angle mort → collision moto quasi-certaine.",
    geste: "Contrôle rétro INTÉRIEUR pour voir l'interfile. Si moto détectée : ne PAS changer de file, la laisser passer, puis reprendre son gabarit.",
  },
];

export default function RetroInterieurSvg() {
  const [scenario, setScenario] = useState(SCENARIOS[0].id);
  const [looking, setLooking] = useState(false); // true = coup d'œil rétro en cours
  const [autoTimer, setAutoTimer] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const tRef = useRef(null);

  const scen = SCENARIOS.find((s) => s.id === scenario);

  // Timer 10 s
  useEffect(() => {
    if (!autoTimer) return undefined;
    tRef.current = setInterval(() => {
      setElapsed((e) => {
        const next = e + 0.1;
        if (next >= 10) {
          setLooking(true);
          setTimeout(() => setLooking(false), 900);
          return 0;
        }
        return next;
      });
    }, 100);
    return () => clearInterval(tRef.current);
  }, [autoTimer]);

  const glance = () => {
    setLooking(true);
    setTimeout(() => setLooking(false), 1200);
  };

  const reset = () => {
    setAutoTimer(false);
    setLooking(false);
    setElapsed(0);
  };

  return (
    <div className="w-full">
      {/* Barre d'outils */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={glance}
          data-testid="o2-glance"
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-cyan-500 text-cyan-950 font-bold text-[12px] hover:bg-cyan-400"
        >
          <Eye size={13} /> Coup d&apos;œil rétro
        </button>
        {!autoTimer ? (
          <button
            type="button"
            onClick={() => { setElapsed(0); setAutoTimer(true); }}
            data-testid="o2-timer-start"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-[12px] font-semibold"
          >
            <Play size={13} /> Timer 10 s
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setAutoTimer(false)}
            data-testid="o2-timer-stop"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-amber-500 text-amber-950 font-bold text-[12px]"
          >
            <Pause size={13} /> Pause timer
          </button>
        )}
        <button
          type="button"
          onClick={reset}
          data-testid="o2-reset"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[12px] font-semibold"
        >
          <RotateCcw size={13} /> Reset
        </button>
        <div className="ml-auto flex items-center gap-1.5">
          {SCENARIOS.map((s) => (
            <button
              key={s.id}
              onClick={() => setScenario(s.id)}
              data-testid={`o2-scenario-${s.id}`}
              className={`h-9 px-3 rounded-full border text-[11px] font-mono-spec font-bold ${
                scenario === s.id ? 'text-white' : 'text-white/60 border-white/15'
              }`}
              style={scenario === s.id ? { background: s.color + '25', borderColor: s.color } : {}}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#0f1a2b] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Vue subjective avec rétroviseur intérieur">
            <defs>
              <linearGradient id="o2-sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e3a5f" />
                <stop offset="100%" stopColor="#3b5f80" />
              </linearGradient>
              <linearGradient id="o2-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#334155" />
                <stop offset="100%" stopColor="#0f172a" />
              </linearGradient>
              <linearGradient id="o2-retro-glow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fbbf24" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
            </defs>

            {/* Route AVANT (fond) */}
            <rect x={0} y={0} width={800} height={140} fill="url(#o2-sky)" />
            <polygon points="0,560 800,560 560,140 240,140" fill="url(#o2-road)" />
            <line x1={400} y1={140} x2={400} y2={560} stroke="#fbbf24" strokeWidth={3} strokeDasharray="14 10" opacity={0.7} />
            {/* Voiture devant */}
            <g transform="translate(400 350)">
              <rect x={-40} y={-25} width={80} height={50} rx={10} fill="#475569" stroke="#0b1120" strokeWidth={2} />
              <rect x={-30} y={-18} width={60} height={20} rx={4} fill="#1e293b" />
              <circle cx={-25} cy={22} r={8} fill="#0b1120" />
              <circle cx={25} cy={22} r={8} fill="#0b1120" />
              {/* Feux stop */}
              <rect x={-38} y={-2} width={6} height={10} fill="#ef4444" />
              <rect x={32} y={-2} width={6} height={10} fill="#ef4444" />
            </g>

            {/* RÉTROVISEUR INTÉRIEUR — cadre trapèze en haut au centre */}
            <g transform="translate(400 50)">
              {/* Ombre/bord */}
              <path
                d="M -120 -30 L 120 -30 L 140 40 L -140 40 Z"
                fill="#0b1120"
                stroke={looking ? '#fbbf24' : '#334155'}
                strokeWidth={looking ? 4 : 2.5}
                filter={looking ? 'drop-shadow(0 0 12px #fbbf24)' : ''}
              />
              {/* Vue arrière (à l'intérieur du rétro) */}
              <clipPath id="o2-retro-clip">
                <path d="M -117 -28 L 117 -28 L 137 38 L -137 38 Z" />
              </clipPath>
              <g clipPath="url(#o2-retro-clip)">
                {/* Ciel arrière */}
                <rect x={-140} y={-30} width={280} height={20} fill="#3b5f80" />
                {/* Route arrière (perspective inversée) */}
                <polygon points="-140,40 140,40 60,-10 -60,-10" fill="#334155" />
                <line x1={0} y1={-10} x2={0} y2={40} stroke="#fbbf24" strokeWidth={2} strokeDasharray="6 4" opacity={0.6} />

                {/* Véhicules derrière selon scenario */}
                {scen.behind.cars.map((c, i) => {
                  const dy = 40 - (c.dist / 30) * 45;
                  const scale = 0.4 + (c.dist / 30) * 0.6;
                  const dx = c.lane === 'left' ? -30 * scale : c.lane === 'right' ? 30 * scale : 0;
                  return (
                    <g key={i} transform={`translate(${dx} ${dy}) scale(${scale})`}>
                      {c.type === 'moto' ? (
                        <>
                          <rect x={-6} y={-14} width={12} height={20} rx={4} fill={c.color} stroke="#0b1120" strokeWidth={1} />
                          <circle cx={0} cy={-16} r={4} fill="#f5c9a2" stroke="#0b1120" strokeWidth={0.5} />
                          <circle cx={0} cy={8} r={3} fill="#fbbf24" />
                        </>
                      ) : (
                        <>
                          <rect x={-16} y={-10} width={32} height={22} rx={5} fill={c.color} stroke="#0b1120" strokeWidth={1} />
                          <rect x={-12} y={-6} width={24} height={10} rx={2} fill="#1e293b" />
                          <rect x={-14} y={10} width={4} height={4} fill="#fbbf24" />
                          <rect x={10} y={10} width={4} height={4} fill="#fbbf24" />
                        </>
                      )}
                    </g>
                  );
                })}

                {/* Message si rien derrière */}
                {scen.behind.cars.length === 0 && (
                  <text x={0} y={22} textAnchor="middle" fill="#94a3b8" fontSize={9} fontStyle="italic">
                    (route dégagée)
                  </text>
                )}
              </g>
              {/* Label au-dessus */}
              <text x={0} y={-38} textAnchor="middle" fill={looking ? '#fbbf24' : '#94a3b8'} fontSize={10} fontWeight="bold" fontFamily="monospace">
                RÉTROVISEUR INTÉRIEUR
              </text>
              {/* Indicateur clignotant si looking */}
              {looking && (
                <g>
                  <circle cx={-155} cy={5} r={5} fill="#fbbf24">
                    <animate attributeName="opacity" values="1;0.2;1" dur="0.4s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={155} cy={5} r={5} fill="#fbbf24">
                    <animate attributeName="opacity" values="1;0.2;1" dur="0.4s" repeatCount="indefinite" />
                  </circle>
                </g>
              )}
            </g>

            {/* Cône du regard vers le rétro */}
            {looking && (
              <line
                x1={400} y1={600}
                x2={400} y2={90}
                stroke="#fbbf24"
                strokeWidth={2}
                strokeDasharray="6 4"
                opacity={0.6}
              />
            )}

            {/* Volant/tableau de bord */}
            <rect x={0} y={560} width={800} height={60} fill="#0b1120" />
            <path
              d="M 250 610 Q 400 570 550 610 L 550 620 L 250 620 Z"
              fill="#1e293b"
              stroke="#334155"
              strokeWidth={2}
            />
            <circle cx={400} cy={605} r={22} fill="none" stroke="#475569" strokeWidth={3} />
            <text x={400} y={609} textAnchor="middle" fill="#475569" fontSize={10} fontWeight="bold">CV</text>

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.55} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">O2 · Coup d&apos;œil rétro intérieur</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC C1h — Prise d&apos;information arrière</text>
            </g>
          </svg>

          {/* Timer 10s bar */}
          <div className="p-4 border-t border-white/10 bg-black/40">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-white/60 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold flex items-center gap-1.5">
                <ArrowUpDown size={12} /> Timer coup d&apos;œil (10 s)
              </span>
              <span className="font-mono-spec text-white text-[13px] font-bold">
                {elapsed.toFixed(1)}<span className="text-white/40 text-[10px]">/10 s</span>
              </span>
            </div>
            <div className="w-full h-3 rounded-full bg-black/60 border border-white/10 overflow-hidden">
              <div
                className="h-full transition-all duration-100"
                style={{
                  width: `${(elapsed / 10) * 100}%`,
                  background: elapsed > 8 ? '#fbbf24' : '#22c55e',
                }}
              />
            </div>
            <div className="mt-1 text-white/40 text-[10.5px]">
              Un coup d&apos;œil rétro intérieur toutes les 10 s = automatisme du bon conducteur.
            </div>
          </div>
        </div>

        {/* Panneau latéral */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="o2-panel">
          <div className="rounded-xl border-2 p-3.5" style={{ borderColor: scen.color, background: scen.color + '15' }}>
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: scen.color }}>
              Scénario actuel
            </div>
            <div className="mt-0.5 font-serif-editorial text-white text-[19px] leading-tight">
              {scen.label}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] p-3">
            <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-1">
              👁 Dans le rétro
            </div>
            <div className="text-white text-[13.5px] font-semibold">{scen.behind.title}</div>
            <p className="mt-1 text-white/70 text-[12px] leading-relaxed">{scen.behind.details}</p>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Diagnostic</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{scen.diagnostic}</p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Le bon geste</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{scen.geste}</p>
            </div>
          </div>

          <div className="mt-4 rounded-md border border-cyan-500/30 bg-cyan-950/20 p-2.5 text-[11.5px] text-cyan-100 leading-relaxed">
            <span className="text-cyan-300 font-mono-spec text-[10px] uppercase tracking-wider font-bold block mb-0.5">Astuce mémoire</span>
            « Devant, derrière, devant » — répète-le en boucle. Le regard revient TOUJOURS devant.
          </div>
        </div>
      </div>
    </div>
  );
}
